import React, { useState, useEffect, Suspense } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, makeStyles, Tooltip, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { uploadWorkItems, getWorkItemType, importWorkItem } from 'utils/WorkItemsUtils';
import { useAppContext } from 'hooks/useAppContext';
import AssignWorkItemType from 'components/WorkItems/AssignWorkItemType';
import { deepCopy } from 'utils/SchemaUtils';
import { WorkItem } from 'classes';
import FormProvider from 'components/Materials/FormProvider';
import ImportToolbar from 'components/Materials/ImportToolbar';
import AuthCheck from 'components/Shared/AuthCheck';

const Grid = React.lazy(() => import('./AddWorkItemGrid'));

const useStyles = makeStyles((theme) => ({
    cardHeader: {
        "background": theme.palette.primary.main,
        "color": theme.palette.primary.contrastText
    },
    paper: { minWidth: "600px" },
}))

function convertImportResultToWorkItem(importResult) {
    return importResult.data.map(wi => {
        const record = {};
        record.WorkItemId = wi.workItemId
        record.Description = wi.description
        record.WorkItemNumber = wi.workItemNumber
        record.ItemCode = wi.itemCode
        record.Quantity = wi.quantity
        record.Units = wi.units
        record.UnitPrice = wi.unitPrice
        record.WitypeId = wi.witypeId
        record.WorkItemsStatusId = wi.workItemsStatusId;
        record.Description_WorkItemsStatus = wi.workItemsStatus.description;
        record.ProjectId = wi.projectId;
        record.IsActive = wi.isActive;
        const workItem = new WorkItem({...record});
        return workItem;
    })
}

const ImportWorkItems = ({ callback }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [importedData, setImportedData] = useState([]);
    const [message, setMessage] = useState("");
    const [selectedWorkItem, setSelectedWorkItem] = useState([]);
    const [checkedRecords, setCheckedRecords] = useState([]);
    const [columns, setColumns] = useState(deepCopy(WorkItem.columns));
    const { selectedProject } = useAppContext();
    const [gridLoading, setGridLoading] = useState(true);
    const title = 'Import Work Items';

    useEffect(() => {
        setGridLoading(true);
        setColumns(_columns => {
            const newColumns = deepCopy(_columns);
            newColumns.find(r => r.field === "Delete").hidden = false;
            newColumns.find(r => r.field === "Error").render = (r) => r.Error ? <Tooltip title={r.Error} placement="top"><ErrorOutlineIcon color="error" /></Tooltip> : null;
            if (importedData.find(r => !!r.Error)) { newColumns.find(r => r.field === "Error").hidden = false; };
            return newColumns;
        });
        setGridLoading(false);
    }, [importedData, message]);

    const handleFormProviderClose = () => {
        setOpen(false)
    };

    const handleErrorRecords = async (errorData) => {
        try {
            const parsedErrorData = JSON.parse(errorData);
            setGridLoading(true);
            await new Promise(res => setTimeout(res, 1000));
            setImportedData(oldImportedData => {
                const newData = [];
                oldImportedData.forEach((value, index) => {
                    const newVal = value;
                    const isExisting = parsedErrorData.Existing.find(r => newVal.WorkItemId === r.WorkItemId);
                    if (isExisting) {
                        newVal.Error = 'Record already exists. Provide a new item #.';
                    };
                    const isError = parsedErrorData['Item Errors'].find(r => newVal.WorkItemId === r.WorkItemId);
                    console.log(isError);
                    if (isError) {
                        let message = 'Attribute error.'
                        if(isError.errorMsg) {
                            message = `${message} ${isError.errorMsg}`;
                        }
                        newVal.Error = message;
                    };

                    if (!isExisting && !isError) {
                        newVal.Error = undefined;
                    };

                    newData.push(newVal);
                });
                return newData;
            });
        } catch (e) {
            setGridLoading(false);
            return e;
        };
        setGridLoading(false);
    }

    const getFormPath = () => import('components/WorkItems/EditImportWorkItem');

    const updateWorkItems = (data) => {
        setOpen(true)
        setSelectedWorkItem(data)
    }

    const updateWorkItemsType = (values) => {
        //update the imported data with the selectedWorkItems and values
        getWorkItemType().then(data => {
            data.map(type => {
                if (type.domainId === values.WitypeId) {
                    selectedWorkItem.map(sItem => {
                        sItem.WitypeId = type.domainId
                        sItem.Description_Witype = type.description
                    })
                    const map = new Map(selectedWorkItem.map(o => [o.WorkItemId, o]))
                    const result = importedData.map(o => Object.assign({}, o, map.get(o.WorkItemId)));
                    setImportedData(result)
                }
            })
            handleFormProviderClose()
        })
    };

    const deleteImportedRecord = async (record) => {
        setGridLoading(true);
        setImportedData(importedData.filter(r => r.WorkItemId !== record.WorkItemId));
        await new Promise(res => setTimeout(res, 1000));
        setGridLoading(false);
    }

    const updateImportedRecord = (updatedRecord) => {
        setGridLoading(true);
        setImportedData(_oldData => {
            const newData = _oldData.map(old => {
                if (old.WorkItemId === updatedRecord.WorkItemId) {
                    return updatedRecord;
                } else {
                    return old
                };
            });
            return newData;
        });
        setGridLoading(false);
    }

    const selection = { "display": true, "callback": updateWorkItems, "tooltip": "Update Work Item Type", "btnText": "Work Item Type" };

    const getRecords = React.useCallback(async () => {
        await new Promise(res => setTimeout(res, 1000));
        return new Promise(res => res({ data: importedData, page: 0, totalCount: importedData.length }))
    }, [importedData]);

    return (
        <AuthCheck action="create" scope="WorkItems" showWarning={true}>
            <div style={{ marginTop: 12 }}>
                <ImportToolbar
                    moduleName="Work Items"
                    importedDataStore={importedData}
                    updateImportedDataStore={setImportedData}
                    updateStatusFunction={setMessage}
                    uploadFileAjaxFunction={uploadWorkItems}
                    saveDataAjaxFunction={importWorkItem}
                    convertDataFunction={convertImportResultToWorkItem}
                    handleUploadError={handleErrorRecords}
                    successfulSaveCallback={callback}
                    selectedRecords={checkedRecords}
                />
                <div style={{ textAlign: 'left', paddingLeft: '20px', paddingTop: '20px' }}>
                    {message.length > 0 && <Alert severity="error" style={{ maxWidth: 500, margin: 4 }}><AlertTitle>Error</AlertTitle>{message}</Alert>}
                </div>
                <FormProvider.Provider value={open}>
                    <Dialog open={open} onClose={handleFormProviderClose} disableBackdropClick maxWidth={'lg'} classes={{ paper: classes.paper }} >
                        <DialogTitle id="form-dialog-title" className={classes.cardHeader} >Work Items Type</DialogTitle>
                        <DialogContent>
                            <div>
                                <AssignWorkItemType CRUDType="INSERT" updateWorkItemsType={updateWorkItemsType} />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleFormProviderClose} color="primary"> Cancel</Button>
                        </DialogActions>
                    </Dialog>
                </FormProvider.Provider>
                <div style={{ paddingTop: '20px' }}>
                    {importedData.length > 0 && !gridLoading ?
                        <Suspense fallback={<div>Loading...</div>}>
                            <Grid
                                columns={columns}
                                title={title}
                                key={gridLoading}
                                updateImportedRecord={updateImportedRecord}
                                selectedProject={selectedProject}
                                component="Materials"
                                formPath={getFormPath}
                                paging={false}
                                data={getRecords}
                                selection={selection}
                                deleteImportedRecord={deleteImportedRecord}
                                onSelectionChange={(rows) => setCheckedRecords(rows)}
                            />
                        </Suspense>
                        // <MaterialTable columns={columns} title={title} data={getRecords} />
                        :
                        <Typography color="textSecondary" gutterBottom style={{ paddingLeft: '25px', paddingBottom: '15px' }}>
                            {gridLoading ? 'loading...' : 'No records imported !'}
                        </Typography>
                    }
                </div>
            </div>
        </AuthCheck>
    )
}


export default ImportWorkItems;